import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Dashboard = ({ generatedPrompt, promptData }) => {
  const { FiEdit, FiImage, FiMessageSquare, FiFileText, FiVideo, FiBookOpen } = FiIcons;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-white mb-4">
          Global Inclusive Travel Content Ecosystem
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          Create authentic, diverse travel content that represents all travelers regardless of age, ability, gender identity, or background
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        <Link to="/generator" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiEdit} className="text-3xl text-yellow-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Content Generator</h2>
          <p className="text-gray-300">Create customized inclusive travel content for all travelers</p>
        </Link>

        <Link to="/blog" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiBookOpen} className="text-3xl text-green-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Blog Creator</h2>
          <p className="text-gray-300">Transform your prompts into engaging blog posts with diverse representation</p>
        </Link>

        <Link to="/carousel" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiImage} className="text-3xl text-blue-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Carousel Builder</h2>
          <p className="text-gray-300">Design engaging Instagram carousels with inclusive imagery</p>
        </Link>

        <Link to="/captions" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiMessageSquare} className="text-3xl text-purple-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Caption Generator</h2>
          <p className="text-gray-300">Create compelling captions for your social media posts</p>
        </Link>

        <Link to="/media-kit" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiFileText} className="text-3xl text-orange-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Media Kit</h2>
          <p className="text-gray-300">Generate professional media kits for brand partnerships</p>
        </Link>

        <Link to="/tiktok" className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors">
          <SafeIcon icon={FiVideo} className="text-3xl text-pink-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">TikTok Script</h2>
          <p className="text-gray-300">Create engaging TikTok scripts with proper representation</p>
        </Link>
      </div>

      {generatedPrompt && (
        <div className="bg-gray-800 rounded-xl p-6 mb-10">
          <h2 className="text-xl font-bold text-white mb-4">Current Generated Prompt</h2>
          <div className="bg-gray-700 rounded-lg p-4">
            <p className="text-gray-300 whitespace-pre-line">{generatedPrompt}</p>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-400">Location</h3>
              <p className="text-white">{promptData.location || 'Not specified'}</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-400">Vibe</h3>
              <p className="text-white">{promptData.vibe || 'Not specified'}</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-400">Traveler Type</h3>
              <p className="text-white">{promptData.travelerType || 'Not specified'}</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-400">Output Type</h3>
              <p className="text-white">{promptData.outputType || 'Not specified'}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Inclusive Travel Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">🏳️‍🌈</div>
            <h3 className="text-white font-semibold">LGBTQ+ Travel</h3>
            <p className="text-gray-400 text-sm">Safe destinations & resources</p>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">♿</div>
            <h3 className="text-white font-semibold">ADA Accessible</h3>
            <p className="text-gray-400 text-sm">Mobility & accessibility</p>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">👴👵</div>
            <h3 className="text-white font-semibold">Senior Travel</h3>
            <p className="text-gray-400 text-sm">Age-friendly experiences</p>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">👨‍👩‍👧‍👦</div>
            <h3 className="text-white font-semibold">Family Travel</h3>
            <p className="text-gray-400 text-sm">All-ages adventures</p>
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-white mb-4">How It Works</h2>
        <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-4">
          <li>Start by creating a customized prompt in the Content Generator</li>
          <li>Select diverse traveler profiles including LGBTQ+, seniors, and accessibility needs</li>
          <li>Use the Blog Creator to transform your prompt into engaging articles</li>
          <li>Generate content for various platforms (Instagram, TikTok, etc.)</li>
          <li>Enhance with AI-generated images using Artistly</li>
          <li>Publish and manage everything through our integrated platform</li>
        </ol>
      </div>
    </div>
  );
};

export default Dashboard;